'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Link from 'next/link';
import {
  Camera,
  Mail,
  Phone,
  User,
  Lock,
  Bell,
  ClipboardList,
  CheckCircle,
  Clock,
  Download,
  Search,
  Calendar,
  Briefcase,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Avatar,
  Badge,
} from '@/components/ui';
import { StarRating } from '@/components/ui/StarRating';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/components/ui/Toast';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const profileSchema = z.object({
  full_name: z.string().min(2, 'Name must be at least 2 characters'),
  phone: z.string().optional(),
  department: z.string().optional(),
  job_title: z.string().optional(),
  bio: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const passwordSchema = z
  .object({
    current_password: z.string().min(1, 'Current password is required'),
    new_password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ['confirm_password'],
  });

type PasswordFormData = z.infer<typeof passwordSchema>;

const departmentOptions = [
  { value: '', label: 'Select department' },
  { value: 'Facilities', label: 'Facilities' },
  { value: 'IT', label: 'IT' },
  { value: 'Security', label: 'Security' },
  { value: 'Housekeeping', label: 'Housekeeping' },
  { value: 'Grounds', label: 'Grounds' },
  { value: 'Administration', label: 'Administration' },
];

const notificationTypes = [
  { key: 'wo_assigned', label: 'Work order assigned to me' },
  { key: 'wo_status', label: 'Work order status changed' },
  { key: 'wo_comment', label: 'New comment on my work orders' },
  { key: 'sla_breach', label: 'SLA breach warning' },
  { key: 'vendor_quote', label: 'Vendor quote received' },
  { key: 'inspection_due', label: 'Inspection due' },
  { key: 'pm_due', label: 'PM schedule due' },
];

// ---------------------------------------------------------------------------
// Toggle Switch
// ---------------------------------------------------------------------------

function ToggleSwitch({
  enabled,
  onToggle,
  label,
}: {
  enabled: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center justify-between w-full py-2 group"
    >
      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      <div
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
          enabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
            enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
          }`}
        />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ProfilePage = () => {
  const { user, updateUser } = useAuthStore();
  const { success } = useToast();

  // Form state
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: user?.full_name ?? '',
      phone: user?.phone ?? '',
      department: user?.department ?? '',
      job_title: '',
      bio: '',
    },
  });

  // Password form
  const {
    register: registerPw,
    handleSubmit: handlePwSubmit,
    formState: { errors: pwErrors },
    reset: resetPw,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  // Notification state
  const [emailNotif, setEmailNotif] = useState(true);
  const [pushNotif, setPushNotif] = useState(true);
  const [smsNotif, setSmsNotif] = useState(false);
  const [notifTypes, setNotifTypes] = useState<Record<string, boolean>>({
    wo_assigned: true,
    wo_status: true,
    wo_comment: true,
    sla_breach: true,
    vendor_quote: false,
    inspection_due: true,
    pm_due: true,
  });

  const toggleNotifType = (key: string) => {
    setNotifTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Handlers
  const onSubmitProfile = (data: ProfileFormData) => {
    updateUser({ full_name: data.full_name, phone: data.phone, department: data.department });
    console.log('[Demo] Profile saved:', data);
    success('Profile Updated', 'Your changes have been saved');
  };

  const onSubmitPassword = (_data: PasswordFormData) => {
    console.log('[Demo] Password change requested');
    success('Password Updated', 'Your password has been changed');
    resetPw();
  };

  const displayName = user?.full_name ?? '';
  const displayEmail = user?.email ?? '';
  const displayRole = user?.role ?? 'manager';
  const displayDept = user?.department ?? '';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-secondary-100">Profile</h1>
        <p className="mt-1 text-gray-500 dark:text-secondary-400">
          Manage your personal information and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* ================================================================ */}
        {/* LEFT COLUMN (2/3)                                                */}
        {/* ================================================================ */}
        <div className="space-y-6 lg:col-span-2">
          {/* Personal Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmitProfile)} className="space-y-5">
                {/* Avatar Upload */}
                <div className="flex items-center gap-6 pb-5 border-b border-gray-100 dark:border-white/[0.06]">
                  <div className="relative group">
                    <Avatar name={displayName} size="xl" />
                    <button
                      type="button"
                      aria-label="Upload profile photo"
                      className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Camera className="h-6 w-6 text-white" />
                    </button>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Profile Photo
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Click the avatar to upload a new photo. JPG, PNG, or GIF. Max 5MB.
                    </p>
                  </div>
                </div>

                {/* Form Fields */}
                <Input
                  label="Full Name"
                  leftIcon={<User className="h-4 w-4" />}
                  error={errors.full_name?.message}
                  {...register('full_name')}
                />

                <Input
                  label="Email Address"
                  type="email"
                  leftIcon={<Mail className="h-4 w-4" />}
                  value={displayEmail}
                  disabled
                  helperText="Email cannot be changed"
                />

                <Input
                  label="Phone"
                  type="tel"
                  leftIcon={<Phone className="h-4 w-4" />}
                  placeholder="+1 (555) 000-0000"
                  {...register('phone')}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Department
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    {...register('department')}
                  >
                    {departmentOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Job Title"
                  leftIcon={<Briefcase className="h-4 w-4" />}
                  placeholder="e.g. Facilities Manager"
                  {...register('job_title')}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bio / About
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    rows={4}
                    placeholder="Tell others about yourself..."
                    {...register('bio')}
                  />
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                  <Button type="submit" variant="primary" disabled={!isDirty}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Security Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                Security
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePwSubmit(onSubmitPassword)} className="space-y-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Update your password regularly to keep your account secure.
                </p>

                <Input
                  label="Current Password"
                  type="password"
                  leftIcon={<Lock className="h-4 w-4" />}
                  error={pwErrors.current_password?.message}
                  {...registerPw('current_password')}
                />

                <Input
                  label="New Password"
                  type="password"
                  leftIcon={<Lock className="h-4 w-4" />}
                  error={pwErrors.new_password?.message}
                  {...registerPw('new_password')}
                />

                <Input
                  label="Confirm New Password"
                  type="password"
                  leftIcon={<Lock className="h-4 w-4" />}
                  error={pwErrors.confirm_password?.message}
                  {...registerPw('confirm_password')}
                />

                <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                  <Button type="submit" variant="primary">
                    Update Password
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Notification Preferences Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Channel Toggles */}
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Notification Channels
                </p>
                <div className="space-y-1">
                  <ToggleSwitch
                    enabled={emailNotif}
                    onToggle={() => setEmailNotif(!emailNotif)}
                    label="Email notifications"
                  />
                  <ToggleSwitch
                    enabled={pushNotif}
                    onToggle={() => setPushNotif(!pushNotif)}
                    label="Push notifications"
                  />
                  <ToggleSwitch
                    enabled={smsNotif}
                    onToggle={() => setSmsNotif(!smsNotif)}
                    label="SMS notifications"
                  />
                </div>
              </div>

              {/* Type Checkboxes */}
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Notification Types
                </p>
                <div className="space-y-2">
                  {notificationTypes.map((nt) => (
                    <label
                      key={nt.key}
                      className="flex items-center gap-3 py-1.5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={notifTypes[nt.key] || false}
                        onChange={() => toggleNotifType(nt.key)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {nt.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                <Button
                  variant="primary"
                  onClick={() => {
                    console.log('[Demo] Notification prefs saved:', {
                      channels: { email: emailNotif, push: pushNotif, sms: smsNotif },
                      types: notifTypes,
                    });
                    success('Preferences Saved', 'Notification settings updated');
                  }}
                >
                  Save Preferences
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ================================================================ */}
        {/* RIGHT COLUMN (1/3)                                               */}
        {/* ================================================================ */}
        <div className="space-y-6">
          {/* Profile Preview Card */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                Profile Preview
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
                How others see your profile
              </p>

              <div className="flex flex-col items-center text-center">
                <Avatar name={displayName} size="xl" />
                <h3 className="mt-3 text-lg font-semibold text-gray-900 dark:text-white">
                  {displayName}
                </h3>
                <div className="flex items-center gap-2 mt-2">
                  <Badge color="blue" size="sm">
                    {displayRole}
                  </Badge>
                  {displayDept && (
                    <Badge color="purple" size="sm">
                      {displayDept}
                    </Badge>
                  )}
                </div>
                <div className="mt-3">
                  <StarRating rating={0} size="sm" />
                </div>
              </div>

              <div className="mt-5 pt-5 border-t border-gray-100 dark:border-white/[0.06] space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">{displayEmail}</span>
                </div>
                {user?.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-700 dark:text-gray-300">{user.phone}</span>
                  </div>
                )}
                {user?.created_at && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-700 dark:text-gray-300">
                      Member since{' '}
                      {new Date(user.created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Stats Card */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                Your Stats
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm text-blue-700 dark:text-blue-400">WOs Created</span>
                  </div>
                  <span className="text-lg font-bold text-blue-700 dark:text-blue-400">—</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-green-50 dark:bg-green-900/20">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm text-green-700 dark:text-green-400">
                      WOs Completed
                    </span>
                  </div>
                  <span className="text-lg font-bold text-green-700 dark:text-green-400">—</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-purple-50 dark:bg-purple-900/20">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    <span className="text-sm text-purple-700 dark:text-purple-400">
                      Avg Response
                    </span>
                  </div>
                  <span className="text-lg font-bold text-purple-700 dark:text-purple-400">—</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-amber-700 dark:text-amber-400">Star Rating</span>
                  </div>
                  <StarRating rating={0} size="sm" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
                Quick Actions
              </h2>
              <div className="space-y-2">
                <Link href="/work-orders">
                  <Button variant="outline" className="w-full justify-start">
                    <ClipboardList className="h-4 w-4 mr-2" />
                    View My Work Orders
                  </Button>
                </Link>
                <Link href="/inspections">
                  <Button variant="outline" className="w-full justify-start">
                    <Search className="h-4 w-4 mr-2" />
                    View My Inspections
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    console.log('[Demo] Download my data requested');
                    success('Download Started', 'Your data export will be ready shortly');
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download My Data
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export { ProfilePage };
